import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardList, CheckCircle2, Loader2, Package, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function POSStoreManagerGRN() {
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [receivingQty, setReceivingQty] = useState({});
  const token = localStorage.getItem('pos_token');

  useEffect(() => {
    if (!token) {
      navigate('/pos/login');
      return;
    }
    fetchPOs();
  }, []);

  const fetchPOs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pos/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setPos(data || []);
    } catch {
      toast.error('Failed to load POs');
    } finally {
      setLoading(false);
    }
  };

  const openReceive = (po) => {
    const init = {};
    po.items?.forEach((it) => {
      init[it.product_id] = it.quantity - (it.received_quantity || 0);
    });
    setReceivingQty(init);
    setActive(po);
  };

  const confirmReceive = async () => {
    const items = Object.entries(receivingQty)
      .filter(([, q]) => parseInt(q) > 0)
      .map(([product_id, q]) => ({ product_id, received_quantity: parseInt(q) }));

    if (items.length === 0) {
      toast.error('Enter at least one quantity');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/pos/purchase-orders/${active._id}/receive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed');
      }
      toast.success('Stock received and updated');
      setActive(null);
      fetchPOs();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="pos-grn">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/pos')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Receive Stock (GRN)</h1>
            <p className="text-sm text-gray-500">Pending purchase orders from admin</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          </div>
        ) : pos.length === 0 ? (
          <div className="bg-white rounded-xl p-12 border text-center text-gray-500">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No pending purchase orders</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pos.map((po) => (
              <div key={po._id} className="bg-white rounded-xl p-5 border shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{po.po_number}</h3>
                    <p className="text-sm text-gray-500">From: {po.supplier_name}</p>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      po.status === 'received'
                        ? 'bg-emerald-100 text-emerald-700'
                        : po.status === 'partial'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {po.status?.toUpperCase()}
                  </span>
                </div>
                <div className="text-sm text-gray-600 mb-3">
                  {po.items?.length || 0} items • ₹{po.total_amount?.toFixed(2)}
                </div>
                {po.status !== 'received' && (
                  <Button
                    onClick={() => openReceive(po)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`receive-po-${po._id}`}
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Receive Stock
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Receive Modal */}
      {active && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Receive {active.po_number}</h2>
              <button onClick={() => setActive(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              {active.items?.map((it) => {
                const remaining = it.quantity - (it.received_quantity || 0);
                return (
                  <div key={it.product_id} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium">{it.product_name}</p>
                        <p className="text-xs text-gray-500">
                          Ordered: {it.quantity} • Already received: {it.received_quantity || 0} •
                          Remaining: {remaining}
                        </p>
                      </div>
                    </div>
                    <Input
                      type="number"
                      max={remaining}
                      min={0}
                      value={receivingQty[it.product_id] || 0}
                      onChange={(e) =>
                        setReceivingQty({ ...receivingQty, [it.product_id]: e.target.value })
                      }
                      placeholder="Qty received now"
                    />
                  </div>
                );
              })}
            </div>

            <div className="flex gap-3 mt-5">
              <Button onClick={confirmReceive} className="flex-1 bg-emerald-600 text-white" data-testid="confirm-receive">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Confirm Receive
              </Button>
              <Button variant="outline" onClick={() => setActive(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
